import { Channel, ChannelConnectParams, ChannelStatus } from 'core/models/channel';
import { LatencyData } from 'core/models/latency';

export class MultiplexedChannel implements Channel {
    get mediaDeviceId(): string {
        return this.channels[0].mediaDeviceId;
    }

    currentStatus: ChannelStatus | undefined;

    constructor(public readonly channels: Channel[]) { }

    async prepare(): Promise<void> {
        await Promise.all(this.channels.map(ch => ch.prepare()));
    }

    async connect(params: ChannelConnectParams): Promise<MediaStream> {
        const streams = await Promise.all(this.channels.map(async ch => await ch.connect(params)));
        return streams[0];
    }

    async disconnect(): Promise<void> {
        await Promise.all(this.channels.map(ch => ch.disconnect()));
    }

    addStatusListener(listener: (status: ChannelStatus) => void): void {
        this.channels.map(ch => ch.addStatusListener(listener));
    }

    removeStatusListener(listener: (status: ChannelStatus) => void): void {
        this.channels.map(ch => ch.removeStatusListener(listener));
    }

    private listenersMapper = new Map<(stream: MediaStream, index: number) => void, Array<(stream: MediaStream) => void>>();

    addReconnectListener(listener: (stream: MediaStream, index: number) => void): void {
        const callbacks = new Array<(stream: MediaStream) => void>();

        this.channels.map((ch, i) => {
            const cb = (stream: MediaStream) => {
                listener(stream, i);
            };
            callbacks.push(cb);
            ch.addReconnectListener(cb);
        });

        this.listenersMapper.set(listener, callbacks);
    }

    removeReconnectListener(listener: (stream: MediaStream, index: number) => void): void {
        const callbacks = this.listenersMapper.get(listener);
        this.listenersMapper.delete(listener);

        if (!callbacks) return;

        callbacks.forEach((cb, i) => {
            this.channels[i].removeReconnectListener(cb);
        });
    }

    get isRecordable(): boolean {
        return this.channels.some(ch => ch.isRecordable);
    }

    async enableRecording(enabled: boolean): Promise<void> {
        await Promise.all(this.channels.map(ch => ch.enableRecording(enabled)));
    }

    async getStats(selector?: MediaStreamTrack): Promise<RTCStatsReport | undefined> {
        const reports = await Promise.all(this.channels.map(ch => ch.getStats(selector)));
        return reports[0];
    }

    getLatency(): LatencyData | undefined {
        return this.channels[0].getLatency();
    }
}
